import { ChangeDetectorRef, Component, EventEmitter, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';
import { detailExpand } from '../../../shared-components/angular-material/mat-animations';
import { Attachment } from '../../../shared-components/models/attachment.model';
import { StudentService } from '../../../shared-components/student/student.service';
import { System } from '../../../shared-components/System';
import { YearService } from '../../../shared-components/year/year.service';
import { ProgramsAndServicesFilter } from '../../../student360/programs-and-services/programs-and-services-filter';
import { ProgramAndService } from '../../../student360/programs-and-services/programs-and-services.model';
import { ProgramServiceConfig } from '../../district/models/program-service.model';
import { DeleteProgramServiceDialogComponent } from '../delete-program-service-dialog/delete-program-service-dialog.component';
import { ProgramAndServiceService } from '../program-and-service.service';
import { AddEditProgramServiceSingleStudentComponent } from './add-edit-program-service-single-student/add-edit-program-service-single-student.component';

@AutoUnsubscribe()
@Component({
    selector: 'single-student',
    templateUrl: './single-student.component.html',
    styleUrls: ['./single-student.component.css'],
    animations: [
        detailExpand
    ]
})
export class SingleStudentComponent implements OnInit, OnDestroy {
    // Properties
    private _sort: MatSort;
    @ViewChild('filterMenu', { read: TemplateRef }) filterMenu: TemplateRef<any>;
    subscriptions: Subscription[] = [];
    programsAndServices: ProgramAndService[];
    originalProgramsAndServices: ProgramAndService[];
    tableData = new TylerMatTableDataSource();
    columns = ['More', 'ProgramName', 'ServiceName', 'EntryDate', 'ExitDate', 'Comments', 'Attachments', 'Actions'];
    junkDate = new Date('0001-01-01T00:00:00');
    toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
    gridHeight = 0;
    expanded: { [key: number]: boolean } = {};
    showFilter = false;
    filter: ProgramsAndServicesFilter;
    previousFilter: ProgramsAndServicesFilter;
    userCanEditPrograms = false;
    programCodes: ProgramServiceConfig[] = [];
    isProgramRowsExpanded = false;
    showInactiveProgram = 2;
    selectedRow: ProgramAndService;
    regEnrollHeight = '';

    // Class Functions
    constructor(
        private dialog: MatDialog,
        private programsAndServicesService: ProgramAndServiceService,
        private studentService: StudentService,
        private yearService: YearService,
        private changeDetector: ChangeDetectorRef,
        private router: Router
    ) { }

    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatSort) set sort(value: MatSort) {
        this._sort = value;
        if (this.tableData) {
            this.tableData.sort = this._sort;
        }
    }

    private _paginator: MatPaginator;
    @ViewChild(MatPaginator, { static: true }) set paginator(value: MatPaginator) {
        this._paginator = value;
        this.tableData.paginator = value;
    }

    // Event Functions
    ngOnInit() {
        // POST: initializes and obtains the necessary data

        // Initializing data
        this.filter = new ProgramsAndServicesFilter();
        this.previousFilter = new ProgramsAndServicesFilter();
        this.filter.shown = 1;
        this.previousFilter.shown = 0;

        if (this.studentService.selectedProgramName && this.studentService.selectedServiceName) {
            this.filter.programName = this.studentService.selectedProgramName;
            this.filter.serviceName = this.studentService.selectedServiceName;

            this.studentService.selectedProgramName = '';
            this.studentService.selectedServiceName = '';
        }

        // Getting data
        this.getProgramServiceCodesByUserId();
        this.subscriptions.push(
            this.studentService.selectedStudentStream$.subscribe(() => {
                this.getProgramsAndServices();
            }),
            this.toggleAllRows.subscribe(toggle => {
                this.tableData.expandAll(toggle);
            }),
            this.yearService.observableStudent360YearStream$.subscribe(() => {
                this.getProgramServiceCodesByUserId();
            }),
            this.studentService.observableSelectedProgramAndServiceStream.subscribe(() => {
                if (this.studentService.selectedProgramName && this.studentService.selectedServiceName) {
                    this.filter.programName = this.studentService.selectedProgramName;
                    this.filter.serviceName = this.studentService.selectedServiceName;

                    this.studentService.selectedProgramName = '';
                    this.studentService.selectedServiceName = '';

                    this.tableData.removeAllFilters();
                    this.applyFilter();
                    this.changeDetector.detectChanges();
                }
            })
        );
        this.tableData.sort = this.sort;
        this.resizeGrid();
    }

    ngOnDestroy() {
        // POST: ends all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    openFilter() {
        this.showFilter = !this.showFilter;
    }

    clearFilter() {
        // PRE: the user clicks on the clear button in the filter
        // POST: clears the filter data and DOES NOT CLOSE THE FILTER
        let getNewData = false;
        const initializeFilter = new ProgramsAndServicesFilter();
        initializeFilter.shown = 1;
        if (this.filter.shown != initializeFilter.shown) {
            getNewData = true;
        }
        Object.assign(this.filter, initializeFilter);
        if (getNewData) {
            this.getProgramsAndServices();
        } else {
            this.applyFilter();
        }
        this.changeDetector.detectChanges();
    }

    clearFilterFromNotice() {
        // PRE: the user clicks the clear filter link from the notice on the footer
        // POTS: clears the footer and resets ALL of the values (because the filter isn't open)
        let getNewData = false;
        const initializeFilter = new ProgramsAndServicesFilter();
        initializeFilter.shown = 1;
        if (this.filter.shown != initializeFilter.shown) {
            getNewData = true;
        }
        Object.assign(this.filter, initializeFilter);
        this.tableData.removeAllFilters();
        if (getNewData) {
            this.getProgramsAndServices();
        } else {
            this.applyFilter();
        }
        this.changeDetector.detectChanges();
    }

    closeFilter() {
        this.openFilter();
    }

    // Methods
    isFiltered() {
        // POST: returns true if the data is being panelFiltered
        if (this.filter.entryDateStart && this.filter.entryDateStart.toString() == 'Invalid Date') {
            this.filter.entryDateStart = undefined;
        }
        if (this.filter.entryDateEnd && this.filter.entryDateEnd.toString() == 'Invalid Date') {
            this.filter.entryDateEnd = undefined;
        }
        if (this.filter.exitDateStart && this.filter.exitDateStart.toString() == 'Invalid Date') {
            this.filter.exitDateStart = undefined;
        }
        if (this.filter.exitDateEnd && this.filter.exitDateEnd.toString() == 'Invalid Date') {
            this.filter.exitDateEnd = undefined;
        }
        const initialFilter = new ProgramsAndServicesFilter();
        return !System.isEqual(this.filter, initialFilter) || this.tableData.isFiltered();
    }

    toggleExpanded(rowId: number) {
        // POST: toggles the expansion of that row
        this.expanded[rowId] = !this.expanded[rowId];
    }

    getProgramsAndServices() {
        // POST: uses the services to get programs and services, returning the observable
        const siteYearId = (this.router.url.indexOf('registrationEnroll') == -1) && this.studentService.studentEnrollment ? this.studentService.studentEnrollment.SiteYearId : this.yearService.defaultYear.SiteYearId;
        this.subscriptions.push(
            this.programsAndServicesService.getProgramsAndServices(this.studentService.selectedStudent.Id, this.yearService.student360Year.Id, siteYearId, this.showInactiveProgram).subscribe(programsAndServices => {
                this.programsAndServices = [];
                programsAndServices.forEach(programAndService => {
                    for (let i = 0; i < programAndService.Attachments.length; i++) {
                        programAndService.Attachments[i] = System.convert<Attachment>(programAndService.Attachments[i], new Attachment());
                    }
                    this.programsAndServices.push(System.convert<ProgramAndService>(programAndService, new ProgramAndService()));
                });

                this.programCodes.filter(o => o.UserHasEditPermission == true).forEach(pc => {
                    this.programsAndServices.filter(o => o.ProgramCodeId == pc.Id).forEach(ps => {
                        ps.UserHasEditPermission = true;
                    });
                });

                this.programCodes.filter(o => o.UserHasDeletePermission == true).forEach(pc => {
                    this.programsAndServices.filter(o => o.ProgramCodeId == pc.Id).forEach(ps => {
                        ps.UserHasDeletePermission = true;
                    });
                });

                setTimeout(
                    () => {
                        this.originalProgramsAndServices = _.cloneDeep(this.programsAndServices);
                        this.tableData.data = this.programsAndServices;
                        this.applyFilter();
                        this.expanded = {};
                        if (this.sort) {
                            this.tableData.sort = this.sort;
                        }
                    },
                    0
                );
            })
        );
    }

    hasDetails(programAndService: ProgramAndService): boolean {
        // POST: returns true if the program and service has details
        return programAndService.Comments != '' || (programAndService.Attachments && programAndService.Attachments.length > 0)
            || (programAndService.ProgramAndServicesDetail && programAndService.ProgramAndServicesDetail.length > 0);
    }

    applyFilter() {
        // POST: applies the filter to the data
        let data = this.programsAndServices;

        // Entry Date
        if (this.filter.entryDateStart && this.filter.entryDateStart.toString() != 'Invalid Date') {
            System.roundDateDown(this.filter.entryDateStart);
            data = data.filter(datum =>
                datum.EntryDate >= this.filter.entryDateStart
            );
        }
        if (this.filter.entryDateEnd && this.filter.entryDateEnd.toString() != 'Invalid Date') {
            System.roundDateUp(this.filter.entryDateEnd);
            data = data.filter(datum =>
                datum.EntryDate <= this.filter.entryDateEnd
            );
        }

        // Exit Date
        if (this.filter.exitDateStart && this.filter.exitDateStart.toString() != 'Invalid Date') {
            System.roundDateDown(this.filter.exitDateStart);
            data = data.filter(datum =>
                datum.ExitDate >= this.filter.exitDateStart
            );
        }
        if (this.filter.exitDateEnd && this.filter.exitDateEnd.toString() != 'Invalid Date') {
            System.roundDateUp(this.filter.exitDateEnd);
            data = data.filter(datum =>
                datum.ExitDate <= this.filter.exitDateEnd
            );
        }

        // Show Services active on this date
        if (this.filter.onThisDate && this.filter.onThisDate.toString() != 'Invalid Date') {
            System.roundDateDown(this.filter.onThisDate);
            const that = this;
            data = data.filter(datum =>
                datum.EntryDate <= this.filter.onThisDate && (datum.ExitDate.toString() == that.junkDate.toString() || datum.ExitDate >= this.filter.onThisDate)
            );
        }

        // Program Name
        if (this.filter.programName) {
            data = data.filter(datum =>
                datum.ProgramName.toLowerCase().includes(this.filter.programName.toLowerCase())
            );
        }

        if (this.filter.shown != null) {
            const that = this;
            let fromDate = new Date();
            let toDate = new Date();
            if (this.filter.shown == 0) {
                System.roundDateDown(fromDate);
                System.roundDateDown(toDate);
                data = data.filter(datum =>
                    datum.EntryDate <= fromDate && (datum.ExitDate.toString() == that.junkDate.toString() || datum.ExitDate >= toDate)
                );
            }
            else if (this.filter.shown == 1) {
                fromDate = new Date(this.yearService.defaultYear.StartDate);
                toDate = new Date(this.yearService.defaultYear.EndDate);
                System.roundDateDown(fromDate);
                System.roundDateDown(toDate);
                data = data.filter(datum =>
                    datum.EntryDate <= toDate && (datum.ExitDate.toString() == that.junkDate.toString() || datum.ExitDate >= fromDate)
                );
            }
            else if (this.filter.shown == 2) {
                data = data;
            }
        }

        // Setting the table data
        this.tableData.data = data;
    }

    getProgramServiceCodesByUserId() {
        this.userCanEditPrograms = false;
        this.programsAndServicesService.getProgramServiceCodesByUserId(this.yearService.defaultYear.Id, this.yearService.defaultYear.SchoolID).subscribe(result => {
            this.programCodes = result;
            setTimeout(() => {
                this.getProgramsAndServices();
            }, 0);

            if (this.programCodes.filter(o => o.UserHasEditPermission == true).length > 0) {
                this.userCanEditPrograms = true;
            }
        });
    }

    // Add/Edit Program service events
    addProgram() {
        // POST: opens the add modal
        const programAndService = new ProgramAndService();
        const studentId = this.studentService.selectedStudent.Id;
        // Open add/edit program and service
        const dialogBox = this.dialog.open(AddEditProgramServiceSingleStudentComponent, {
            data: {
                addProgram: true,
                programAndService: programAndService,
                programCodes: this.programCodes,
                studentId: studentId
            },
            disableClose: true,
            minWidth: '120vh',
            maxWidth: '120vh',
        });
        dialogBox.afterClosed().subscribe(newProgram => {
            this.getProgramsAndServices();
        });
    }

    editProgram(program: ProgramAndService) {
        // POST: opens the edit modal for a program
        const studentId = this.studentService.selectedStudent.Id;
        const dialogBox = this.dialog.open(AddEditProgramServiceSingleStudentComponent, {
            data: {
                addProgram: false,
                programAndService: program,
                programCodes: this.programCodes,
                studentId: studentId
            },
            disableClose: true,
            minWidth: '120vh',
            maxWidth: '120vh',
        });
        dialogBox.afterClosed().subscribe(newProgram => {
            this.getProgramsAndServices();
        });
    }

    expandAll(expand: boolean, type: string, dataSource: any) {
        // POST: expands or collapses semesters/terms rows
        if (type === 'program') {
            this.isProgramRowsExpanded = !expand;
            this.tableData.data.forEach(program => {
                program.detailRow = this.isProgramRowsExpanded;
            });
        }
    }

    deleteProgram(program: ProgramAndService) {
        // POST: opens the delete modal for program
        let delMessage = '';
        delMessage = 'Are you sure you want to delete ' + program.ProgramName + ' - ' + program.ServiceName + ' from ' + this.studentService.selectedStudent.LastName + ', ' + this.studentService.selectedStudent.FirstName + '?';
        const dialogBox = this.dialog.open(DeleteProgramServiceDialogComponent, {
            data: {
                id: program.Id,
                type: 'delete',
                title: 'Delete Program/Service',
                message: delMessage
            },
            minWidth: '40vh',
            maxWidth: '55vh',
        });
        dialogBox.afterClosed().subscribe(deleted => {
            if (deleted) {
                this.getProgramsAndServices();
            }
        });
    }

    getInactiveChkStyle() {
        if (this.regEnrollMode()) {
            return { position: 'fixed', right: '70px' };
        }
    }

    regEnrollMode() {
        return this.router.url.toLowerCase().indexOf('registrationenrollment') > -1;
    }

    programAndServiceDetailAttachmentsAriaLabel(attachment: Attachment): string {
        // PRE: a program and service detail to generate an aria label for
        // POST: creates the aria label for the program and service detail's attachment's table row
        return 'Attachment ' + attachment.AttachmentFileName + '. ' +
            'Attachment Description: ' + attachment.AttachmentDescription + '. ' +
            'Attachment Added on: ' + attachment.AttachmentAddedOn.toDateString() + '. ' +
            'Attachment Added by: ' + attachment.AttachmentAddedBy + '. ' +
            'Press enter to view attachment.';
    }

    viewAttachment(attachment: Attachment) {
        // PRE: an attachment to view
        // POST: goes to view that attachment
        if (attachment.DocumentMimeType == 'application/vnd.openxmlformats-officedocument.word') {
            System.saveFile(attachment.DocumentBase64, attachment.DocumentMimeType, attachment.DocumentName);
        } else {
            System.viewFileFromId(attachment.AttachmentId, attachment.AttachmentFileName);
        }
    }

    resizeGrid() {
        this.regEnrollHeight = (window.innerHeight - 226).toString();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event?) {
        this.resizeGrid();
    }

}